/**
 * Страница Contact (02_GPT-MODULAR).
 * Форма обратной связи и информационный блок.
 */

import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { API_BASE_URL } from "../config/constants";
import TeamCards from "../components/TeamCards/TeamCards";
import "./ContactPage.css";

function ContactPage() {
    const location = useLocation();
    const pageRef = useRef(null);
    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        phone: "",
        email: "",
        subject: "",
        message: ""
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus({ type: '', message: '' });

        try {
            const response = await fetch(`${API_BASE_URL}/contact/submit`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData)
            });

            const data = await response.json();

            if (response.ok) {
                setSubmitStatus({ 
                    type: 'success', 
                    message: 'Thank you! Your message has been sent successfully. We will contact you soon.' 
                });
                // Очищаем форму
                setFormData({
                    firstName: "",
                    lastName: "",
                    phone: "",
                    email: "",
                    subject: "",
                    message: ""
                });
            } else {
                throw new Error(data.error || 'Failed to send message');
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            setSubmitStatus({ 
                type: 'error', 
                message: 'Sorry, there was an error sending your message. Please try again or contact us directly.' 
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Scroll to Contact Us form when navigating from home page
    useEffect(() => {
        if (!location.state?.scrollToForm) return;
        window.scrollTo(0, 0);
        const timeoutId = setTimeout(() => {
            if (!pageRef.current) return;
            const formElement = pageRef.current;
            const elementRect = formElement.getBoundingClientRect();
            const absoluteElementTop = elementRect.top + window.pageYOffset;
            const offset = absoluteElementTop - 80;
            const targetPosition = Math.max(0, offset);
            const startPosition = window.pageYOffset;
            const distance = targetPosition - startPosition;
            const duration = 1200;
            let startTime = null;

            function animation(currentTime) {
                if (startTime === null) startTime = currentTime;
                const timeElapsed = currentTime - startTime;
                const progress = Math.min(timeElapsed / duration, 1);
                const ease = progress < 0.5
                    ? 4 * progress * progress * progress
                    : 1 - Math.pow(-2 * progress + 2, 3) / 2;
                window.scrollTo(0, startPosition + distance * ease);
                if (timeElapsed < duration) {
                    requestAnimationFrame(animation);
                }
            }

            requestAnimationFrame(animation);
        }, 400);
        return () => clearTimeout(timeoutId);
    }, [location.state]);

    return (
        <div className="contact-page" ref={pageRef}>
            <TeamCards />

            {/* Join Us section */}
            <div className="contact-header">
                <h1 className="contact-header__title">
                    Join Us in Creating<br />Something Great
                </h1>
            </div>

            {/* Contact Form and Info Container */}
            <div className="contact-container">
                {/* Left side - Contact Form */}
                <div className="contact-form-section">
                    <div className="contact-form-terminal">
                        <div className="contact-form-terminal__header">
                            <span className="contact-form-terminal__title">Contact Us</span>
                            <div className="contact-form-terminal__dots">
                                <span className="contact-form-terminal__dot"></span>
                                <span className="contact-form-terminal__dot"></span>
                                <span className="contact-form-terminal__dot"></span>
                            </div>
                        </div>
                        <div className="contact-form-terminal__body">
                            <form className="contact-form" onSubmit={handleSubmit}>
                        {submitStatus.message && (
                            <div className={`contact-form__status contact-form__status_${submitStatus.type}`}>
                                {submitStatus.message}
                            </div>
                        )}
                        
                        <div className="contact-form__row">
                            <div className="contact-form__field">
                                <input
                                    type="text"
                                    name="firstName"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                    placeholder="First Name *"
                                    required
                                    className="contact-form__input"
                                />
                            </div>
                            <div className="contact-form__field">
                                <input
                                    type="text"
                                    name="lastName"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    placeholder="Last Name *"
                                    required
                                    className="contact-form__input"
                                />
                            </div>
                        </div>

                        <div className="contact-form__row">
                            <div className="contact-form__field">
                                <input
                                    type="tel"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    placeholder="Phone Number *"
                                    required
                                    className="contact-form__input"
                                />
                            </div>
                            <div className="contact-form__field">
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder="Email *"
                                    required
                                    className="contact-form__input"
                                />
                            </div>
                        </div>

                        <div className="contact-form__field contact-form__field_full">
                            <input
                                type="text"
                                name="subject"
                                value={formData.subject}
                                onChange={handleChange}
                                placeholder="Subject *"
                                required
                                className="contact-form__input"
                            />
                        </div>

                        <div className="contact-form__field contact-form__field_full">
                            <textarea
                                name="message"
                                value={formData.message}
                                onChange={handleChange}
                                placeholder="Message *"
                                required
                                className="contact-form__textarea"
                                rows="6"
                            />
                        </div>

                                <button 
                                    type="submit" 
                                    className="contact-form__submit"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? 'Sending...' : 'Send Message'}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>

                {/* Right side - Contact Info */}
                <div className="contact-info">
                    <div className="contact-info__section">
                        <h3 className="contact-info__title">Address</h3>
                        <p className="contact-info__text">
                            Reviusstraat 1k, 8913 HT Leeuwarden
                        </p>
                    </div>

                    <div className="contact-info__section">
                        <h3 className="contact-info__title">Contact</h3>
                        <p className="contact-info__text">
                            Phone : +0123-456-789<br />
                            Email : teamtybe.io@gmail.com
                        </p>
                    </div>

                    <div className="contact-info__section">
                        <h3 className="contact-info__title">Open Time</h3>
                        <p className="contact-info__text">
                            Monday - Friday : 10:00 - 20:00
                        </p>
                    </div>

                    <div className="contact-info__section">
                        <h3 className="contact-info__title">Stay Connected</h3>
                        <div className="contact-info__socials">
                            {/*<a href="#" className="contact-info__social" aria-label="Facebook" target="_blank" rel="noopener noreferrer">
                                <span className="contact-info__social-icon">
                                    <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                                </span>
                            </a>*/}
                            {/*<a href="#" className="contact-info__social" aria-label="Twitter" target="_blank" rel="noopener noreferrer">
                                <span className="contact-info__social-icon">
                                    <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                                </span>
                            </a>*/}
                            {/*<a href="#" className="contact-info__social" aria-label="Instagram" target="_blank" rel="noopener noreferrer">
                                <span className="contact-info__social-icon">
                                    <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                                </span>
                            </a>*/}
                            <a href="https://www.linkedin.com/company/tybe-team" className="contact-info__social" aria-label="LinkedIn" target="_blank" rel="noopener noreferrer">
                                <span className="contact-info__social-icon">
                                    <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                                </span>
                            </a>
                           {/*<a href="#" className="contact-info__social" aria-label="YouTube" target="_blank" rel="noopener noreferrer">
                                <span className="contact-info__social-icon">
                                    <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                                </span>
                            </a>*/}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ContactPage;
// pages/ContactPage.js
